import { IAddProductVariantInput, IAttribute } from "muminsmart-utils";
import { buildVariantCombinationKey } from "./variantCommon";

type MatrixAttribute = {
  attributeId: string;
  attribute: IAttribute;
};

function cartesianProduct<T>(arrays: T[][]): T[][] {
  if (!arrays.length) {
    return [[]];
  }

  return arrays.reduce<T[][]>(
    (acc, curr) => acc.flatMap((a) => curr.map((c) => [...a, c])),
    [[]],
  );
}

export function getMatrixAttributes(
  variantAttributes: Array<{ attribute: IAttribute; value: string }>,
): MatrixAttribute[] {
  return variantAttributes.map(({ attribute }) => ({
    attributeId: attribute.id,
    attribute,
  }));
}

export function parseAttributeValues(input: string): string[] {
  return [
    ...new Set(
      input
        .split(/[,;\n]/)
        .map((value) => value.trim())
        .filter(Boolean),
    ),
  ];
}

export function getEffectiveAttributeValues(
  attributeValues: Record<string, string[]>,
  valueInputs: Record<string, string>,
  matrixAttributes: MatrixAttribute[],
): Record<string, string[]> {
  const effective: Record<string, string[]> = { ...attributeValues };

  for (const attribute of matrixAttributes) {
    const pendingValues = parseAttributeValues(
      valueInputs[attribute.attributeId] ?? "",
    );

    if (!pendingValues.length) {
      continue;
    }

    effective[attribute.attributeId] = [
      ...new Set([
        ...(effective[attribute.attributeId] ?? []),
        ...pendingValues,
      ]),
    ];
  }

  return effective;
}

export function countMatrixCombinations(
  attributeValues: Record<string, string[]>,
  matrixAttributes: MatrixAttribute[],
): number {
  if (!matrixAttributes.length) {
    return 0;
  }

  return matrixAttributes.reduce((total, attribute) => {
    const valueCount = attributeValues[attribute.attributeId]?.length ?? 0;

    if (valueCount === 0) {
      return 0;
    }

    return total * valueCount;
  }, 1);
}

interface GenerateVariantsFromMatrixOptions {
  attributeValues: Record<string, string[]>;
  matrixAttributes: MatrixAttribute[];
  allVariantAttributes: Array<{ attribute: IAttribute; value: string }>;
  existingVariants: IAddProductVariantInput[];
  defaults: {
    price: number;
    discountedPrice: number;
    weight: number;
    stock: number;
    alwaysAvailable: boolean;
    featuredImage?: File | string;
    additionalImages: Array<File | string>;
    bulkPrices: Array<{ quantity: number; price: number }>;
  };
  createSku: (existingSkus: string[]) => string;
}

export function generateVariantsFromMatrix({
  attributeValues,
  matrixAttributes,
  allVariantAttributes,
  existingVariants,
  defaults,
  createSku,
}: GenerateVariantsFromMatrixOptions): IAddProductVariantInput[] {
  const optionArrays = matrixAttributes.map((attribute) =>
    (attributeValues[attribute.attributeId] ?? []).map((value) => ({
      attributeId: attribute.attributeId,
      value,
    })),
  );

  if (optionArrays.some((options) => options.length === 0)) {
    return [];
  }

  const combinations = cartesianProduct(optionArrays);
  const existingByKey = new Map<string, IAddProductVariantInput>();

  for (const variant of existingVariants) {
    const key = buildVariantCombinationKey(variant.variantAttributes ?? []);
    existingByKey.set(key, variant);
  }

  const usedSkus = [
    ...existingVariants.map((variant) => variant.sku),
    ...combinations.map(() => ""),
  ].filter(Boolean);

  return combinations.map((combo) => {
    const fullAttributes = allVariantAttributes.map(({ attribute }) => {
      const matchedOption = combo.find(
        (item) => item.attributeId === attribute.id,
      );

      return {
        attributeId: attribute.id,
        value: matchedOption?.value ?? "",
      };
    });

    const key = buildVariantCombinationKey(fullAttributes);
    const existingVariant = existingByKey.get(key);

    if (existingVariant) {
      return {
        ...existingVariant,
        variantAttributes: fullAttributes,
        ...(defaults.featuredImage
          ? { featuredImage: defaults.featuredImage }
          : {}),
        ...(defaults.additionalImages.length
          ? { additionalImages: [...defaults.additionalImages] }
          : {}),
        ...(defaults.bulkPrices.length
          ? {
              bulkPrices: defaults.bulkPrices.map((bulkPrice) => ({
                quantity: bulkPrice.quantity,
                price: bulkPrice.price,
              })),
            }
          : {}),
      };
    }

    const sku = createSku(usedSkus);
    usedSkus.push(sku);

    return {
      variantAttributes: fullAttributes,
      sku,
      price: defaults.price,
      discountedPrice: defaults.discountedPrice,
      weight: defaults.weight,
      stock: defaults.stock,
      bulkPrices: defaults.bulkPrices.map((bulkPrice) => ({
        quantity: bulkPrice.quantity,
        price: bulkPrice.price,
      })),
      featuredImage:
        defaults.featuredImage ?? (undefined as unknown as File),
      additionalImages: defaults.additionalImages.length
        ? [...defaults.additionalImages]
        : [],
      isActive: true,
      alwaysAvailable: defaults.alwaysAvailable,
    };
  });
}

const SKU_PREFIX = "SKU";

export function createRandomSku(existingSkus: string[]): string {
  const normalizedSkus = new Set(
    existingSkus.map((sku) => sku.trim().toUpperCase()).filter(Boolean),
  );

  let nextSku = "";

  do {
    const randomPart = Math.random().toString(36).slice(2, 8).toUpperCase();
    const uniquePart = Date.now().toString(36).slice(-4).toUpperCase();
    nextSku = `${SKU_PREFIX}-${randomPart}${uniquePart}`;
  } while (normalizedSkus.has(nextSku));

  return nextSku;
}
